/**
 * RTB House — interaction events fired from the theme.
 *
 * The page events (home / category / offer / listing / basketstatus / uid) are
 * rendered server-side by snippets/rtb-house.liquid. This file adds the two
 * interaction events that cannot be rendered server-side because they happen
 * after the page has loaded:
 *
 *   basketadd  — "Shopping Cart - Add To Cart", one event per added product.
 *   startorder — "Order Process Start", fired when the customer leaves for
 *                checkout (cart form submit, or the "Buy it now" button).
 *
 * Neither event needs to run on a checkout page, so both live in the theme
 * rather than in a Customer Events pixel.
 *
 * Configuration comes from window.RTBHouseThemeConfig, written by
 * snippets/rtb-house.liquid from the theme settings.
 */
(function () {
  'use strict';

  if (window.__rtbHouseThemeEventsLoaded) return;
  window.__rtbHouseThemeEventsLoaded = true;

  var config = window.RTBHouseThemeConfig || {};
  // 'product' sends the product id, anything else sends the variant id. Must
  // match the id source used for the 'offer' event in rtb-house.liquid, and the
  // id used in the product feed sent to RTB House.
  var useProductId = config.offerIdSource !== 'variant';

  // Requests carrying this header are cart writes the theme makes on its own
  // (campaign gifts, automatic bag), not a customer adding a product.
  var SKIP_HEADER = 'x-hp-no-rtb';
  var ADD_TO_CART_PATH = /\/cart\/add(\.js)?(?:$|[?#])/;

  function push(event) {
    try {
      (window.rtbhEvents = window.rtbhEvents || []).push(event);
    } catch (error) {
      /* tracking must never break the storefront */
    }
  }

  /* ---------------------------------------------------------------- basketadd */

  function offerIdOf(item) {
    if (!item || typeof item !== 'object') return null;
    var id = useProductId ? item.product_id : item.variant_id || item.id;
    if (id === null || id === undefined || id === '') return null;
    return String(id);
  }

  function trackAddToCart(payload) {
    if (!payload || typeof payload !== 'object') return;

    // /cart/add.js returns the line item for a single add, or { items: [...] }
    // for a bulk add.
    var items = Array.isArray(payload.items) ? payload.items : [payload];

    items.forEach(function (item) {
      var offerId = offerIdOf(item);
      if (!offerId) return;
      push({ eventType: 'basketadd', offerId: offerId });
    });
  }

  function isAddToCartUrl(url) {
    if (!url) return false;
    try {
      return ADD_TO_CART_PATH.test(new URL(String(url), window.location.href).pathname);
    } catch (error) {
      return ADD_TO_CART_PATH.test(String(url));
    }
  }

  function hasSkipHeader(input, init) {
    try {
      if (init && init.headers) {
        var headers = new Headers(init.headers);
        if (headers.has(SKIP_HEADER)) return true;
      }
      if (input && typeof input === 'object' && input.headers && input.headers.has) {
        if (input.headers.has(SKIP_HEADER)) return true;
      }
    } catch (error) {
      /* fall through */
    }
    return false;
  }

  // Every add-to-cart path in this theme goes through fetch, so patching fetch
  // covers the product form, quick add, bundles and complementary products
  // without touching each of them.
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var watch = isAddToCartUrl(url) && !hasSkipHeader(input, init);
      var request = nativeFetch.apply(this, arguments);

      if (!watch) return request;

      return request.then(function (response) {
        try {
          // A 422 means the product was not added (out of stock, limit hit).
          if (response && response.ok) {
            response
              .clone()
              .json()
              .then(trackAddToCart)
              .catch(function () {});
          }
        } catch (error) {
          /* never block the cart response */
        }
        return response;
      });
    };
  }

  // Apps and older scripts may still add to cart over XMLHttpRequest.
  var nativeOpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  if (nativeOpen) {
    window.XMLHttpRequest.prototype.open = function (method, url) {
      this.__rtbIsAddToCart = isAddToCartUrl(url);
      if (this.__rtbIsAddToCart && !this.__rtbListenerBound) {
        this.__rtbListenerBound = true;
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status >= 300) return;
          try {
            trackAddToCart(JSON.parse(this.responseText));
          } catch (error) {
            /* not JSON, nothing to report */
          }
        });
      }
      return nativeOpen.apply(this, arguments);
    };
  }

  /* --------------------------------------------------------------- startorder */

  // The customer can click checkout, come back and click again. RTB House only
  // needs to know the order process started, so fire it once per page view.
  var startOrderSent = false;

  function trackStartOrder() {
    if (startOrderSent) return;
    startOrderSent = true;
    push({ eventType: 'startorder' });
  }

  // Cart page: the checkout button submits the cart form. Listening on the
  // bubble phase means hp-checkout-guard.js has already had its chance to
  // cancel the submit, so a blocked checkout does not report a start.
  document.addEventListener(
    'submit',
    function (event) {
      if (event.defaultPrevented) return;

      var form = event.target;
      if (!form || form.nodeName !== 'FORM') return;

      // event.submitter is the reliable signal. Where it is unavailable, fall
      // back to "this form owns a checkout control" — form.elements also covers
      // buttons associated through the form="cart" attribute.
      var submitter = event.submitter;
      var isCheckout = submitter
        ? submitter.name === 'checkout'
        : !!(form.elements && form.elements.namedItem('checkout'));

      if (!isCheckout) return;
      trackStartOrder();
    },
    false
  );

  // Product page "Buy it now" and the additional checkout buttons skip the cart
  // and go straight to checkout.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var trigger = target.closest(
        '.shopify-payment-button__button, .additional-checkout-buttons button, [name="checkout"]'
      );
      if (!trigger || trigger.disabled) return;

      // A [name="checkout"] submit button is handled by the submit listener
      // above, which knows whether the guard cancelled it.
      if (trigger.name === 'checkout') return;

      trackStartOrder();
    },
    false
  );
})();
